import React, { useState, useEffect, useMemo } from "react";
import { TabContent, TabPane, Row, Col } from "reactstrap";
import countries from "../../Country/country.json";
import { useSelector, useDispatch } from "react-redux";
import { postNewOrder } from "../../redux/thunks/orderThunks";
import { Dropdown } from "primereact/dropdown";
import { useTranslation } from "react-i18next";
import { fetchLatestForexRate } from "../../utils/fetchPolygonRate";

const PostComponent = ({ triggerRefresh }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user.data);
  const paymentCards = user?.bankingInfo?.paymentCards || [];
  const depositAccounts = user?.bankingInfo?.depositAccounts || [];
  const [lastEdited, setLastEdited] = useState(null);

  const [latestFx, setLatestFx] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState(null);

  const valueTemplate = (option, props) => {
    if (!option || !option.flag) return props.placeholder;
    return (
      <div className="p-dropdown-value">
        <img
          alt={option.name}
          src={option.flag}
          width="20"
          style={{ verticalAlign: "middle", marginRight: "8px" }}
        />
        <span>{t(`${option.currencyCode}.currency.label`)}</span>
      </div>
    );
  };

  const itemTemplate = (option) => {
    return (
      <div className="p-dropdown-item">
        <img
          alt={option.name}
          src={option.flag}
          width="20"
          style={{ verticalAlign: "middle", marginRight: "8px" }}
        />
        <span>{t(`${option.currencyCode}.currency.label`)}</span>
      </div>
    );
  };

  const [formState, setFormState] = useState({
    buyCurrency: "",
    sellCurrency: "",
    buyAmount: "",
    sellAmount: "",
    paymentMethod: "",
    depositAccountId: "",
  });

  useEffect(() => {
    const buy = formState?.buyCurrency;
    const sell = formState?.sellCurrency;
    if (!buy || !sell) {
      setLatestFx(null);
      return;
    }
    const ac = new AbortController();
    setFxLoading(true);
    setFxError(null);
    (async () => {
      try {
        const price = await fetchLatestForexRate(buy, sell, {
          signal: ac.signal,
        });
        setLatestFx(price);
        console.log("latest rate", latestFx);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error(e);
          setFxError(e.message || "Failed to fetch FX");
        }
      } finally {
        setFxLoading(false);
      }
    })();
    return () => ac.abort();
  }, [formState.buyCurrency, formState.sellCurrency]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const cleanValue =
      name === "buyAmount" || name === "sellAmount"
        ? value.replace(/[^\d.]/g, "")
        : value;

    setFormState((prev) => ({ ...prev, [name]: cleanValue }));

    if (name === "sellAmount") setLastEdited("sell");
    if (name === "buyAmount") setLastEdited("buy");
  };

  const sellAmount = parseFloat(formState.sellAmount) || 0;
  const buyAmount = parseFloat(formState.buyAmount) || 0;
  const fee = sellAmount * 0.005;
  const total = sellAmount + fee;

  const handlePostOrder = () => {
    const selectedCard = paymentCards.find(
      (card) => `${card.brand} **** ${card.last4}` === formState.paymentMethod
    );
    const selectedAccount = depositAccounts.find(
      (acc) =>
        `${acc.bankName} ****${acc.accountNumber?.slice(-4)}` ===
        formState.depositAccountId
    );

    const rate = sellAmount / buyAmount;
    const orderData = {
      max: sellAmount,
      from: formState.sellCurrency,
      to: formState.buyCurrency,
      rate: rate,
      selectedCard: selectedCard || { id: null },
      selectedAccount: selectedAccount || { bankAccountId: null },
    };

    if (rate / latestFx > 1.02 || rate / latestFx < 0.98) {
      alert("Your rate is not within the allowed range");
      return;
    }

    dispatch(
      postNewOrder({
        orderData,
        userId: user.id,
        email: user.email,
      })
    );
    triggerRefresh();
  };

  if (!user || !user.bankingInfo) return <div>Loading banking info...</div>;

  return (
    <div className="row">
      <div className="col-xl-5 col-lg-5 col-md-12">
        <div className="card">
          <div className="card-body">
            <div className="buy-sell-widget">
              <TabContent activeTab={"1"}>
                <TabPane tabId="1">
                  <form>
                    <Row>
                      <Col xs={5}>
                        <div className="form-group">
                          <label>{t("from.label")}</label>
                          <Dropdown
                            id="sellCurrency"
                            name="sellCurrency"
                            value={formState.sellCurrency}
                            options={countries}
                            onChange={(e) =>
                              setFormState({
                                ...formState,
                                sellCurrency: e.value,
                              })
                            }
                            optionLabel="currencyName"
                            optionValue="currencyCode"
                            className="w-100"
                            filter
                            filterBy="currencyName"
                            placeholder={t("select.label")}
                            valueTemplate={valueTemplate}
                            itemTemplate={itemTemplate}
                          />
                        </div>
                      </Col>
                      <Col
                        xs={2}
                        className="d-flex justify-content-center align-items-center"
                      >
                        <i className="pi pi-arrow-right fs-4" />
                      </Col>
                      <Col xs={5}>
                        <div className="form-group">
                          <label>{t("to.label")}</label>
                          <Dropdown
                            id="buyCurrency"
                            name="buyCurrency"
                            value={formState.buyCurrency}
                            options={countries}
                            onChange={(e) =>
                              setFormState({
                                ...formState,
                                buyCurrency: e.value,
                              })
                            }
                            optionLabel="currencyName"
                            optionValue="currencyCode"
                            className="w-100"
                            filter
                            filterBy="currencyName"
                            placeholder={t("select.label")}
                            valueTemplate={valueTemplate}
                            itemTemplate={itemTemplate}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs={6}>
                        <div className="form-group">
                          <label>{t("sell.amount.label")}</label>
                          <input
                            type="text"
                            name="sellAmount"
                            className="form-control"
                            placeholder={`100${
                              formState.sellCurrency
                                ? " " +
                                  t(`${formState.sellCurrency}.currency.label`)
                                : ""
                            }`}
                            value={formState.sellAmount}
                            onChange={handleChange}
                          />
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="form-group">
                          <label>{t("buy.amount.label")}</label>
                          <input
                            type="text"
                            name="buyAmount"
                            className="form-control"
                            placeholder={`100${
                              formState.buyCurrency
                                ? " " +
                                  t(`${formState.buyCurrency}.currency.label`)
                                : ""
                            }`}
                            value={formState.buyAmount}
                            onChange={handleChange}
                          />
                        </div>
                      </Col>
                    </Row>
                    {(() => {
                      const hasCurrencies =
                        formState.buyCurrency && formState.sellCurrency;
                      const fx = Number(latestFx);
                      if (!hasCurrencies || !Number.isFinite(fx)) return null;

                      const buy = parseFloat(formState.buyAmount) || 0;
                      const sell = parseFloat(formState.sellAmount) || 0;

                      // 你的 fetchLatestForexRate(buy,sell) 逻辑里，之前的写法是：
                      //   “当已知 buyAmount 时，sell 的范围 = buy * latestFx * (0.98~1.02)”
                      //   → 即 sell = buy * rate
                      // 所以反向：
                      //   “当已知 sellAmount 时，buy 的范围 = sell / rate * (1/1.02 ~ 1/0.98)”
                      //   更直观地写：minBuy = sell / (rate*1.02), maxBuy = sell / (rate*0.98)

                      if (lastEdited === "sell" && sell > 0) {
                        const minBuy = sell / (fx * 1.02);
                        const maxBuy = sell / (fx * 0.98);
                        return (
                          <>
                            <div>{`The buying amount has to be within the range of ${minBuy.toFixed(
                              2
                            )} and ${maxBuy.toFixed(2)}`}</div>
                            <br />
                          </>
                        );
                      }

                      if (lastEdited === "buy" && buy > 0) {
                        const minSell = fx * 0.98 * buy;
                        const maxSell = fx * 1.02 * buy;
                        return (
                          <>
                            <div>{`The selling amount has to be within the range of ${minSell.toFixed(
                              2
                            )} and ${maxSell.toFixed(2)}`}</div>
                            <br />
                          </>
                        );
                      }

                      return null;
                    })()}

                    <div className="form-group">
                      <label>{t("payment.method.label")}</label>
                      <select
                        className="form-control"
                        name="paymentMethod"
                        value={formState.paymentMethod}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        {paymentCards.map((card) => (
                          <option
                            key={card.id}
                            value={`${card.brand} **** ${card.last4}`}
                          >
                            {`${card.brand} **** ${card.last4} (exp: ${card.expMonth}/${card.expYear})`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{t("deposit.account.label")}</label>
                      <select
                        className="form-control"
                        name="depositAccountId"
                        value={formState.depositAccountId}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        {depositAccounts.map((account) => (
                          <option
                            key={account.bankAccountId}
                            value={`${
                              account.bankName
                            } ****${account.accountNumber?.slice(-4)}`}
                          >
                            {`${
                              account.bankName
                            } ****${account.accountNumber?.slice(-4)}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      className="btn btn-success btn-block"
                      onClick={handlePostOrder}
                    >
                      {t("post.my.order.label")}
                    </button>
                  </form>
                </TabPane>
              </TabContent>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xl-7 col-lg-7 col-md-12">
        <div className="card">
          <div className="card-body">
            <div className="buyer-seller">
              <h4>{t("your.post.summary.label")}</h4>
              <table className="table">
                <tbody>
                  <tr>
                    <td>{`You will exchange ${formState.sellAmount} ${
                      formState.sellCurrency
                        ? " " + t(`${formState.sellCurrency}.currency.label`)
                        : ""
                    } to ${formState.buyAmount} ${
                      formState.buyCurrency
                        ? " " + t(`${formState.buyCurrency}.currency.label`)
                        : ""
                    }`}</td>
                  </tr>
                  <tr>
                    <td>{`Exchange Rate `}</td>
                    <td>{`${
                      Math.round(
                        (formState.buyAmount / formState.sellAmount) * 1000
                      ) / 1000
                    }`}</td>
                  </tr>
                  {/* <tr>
                    <td>{t("you.want.to.buy.label")}</td>
                    <td>
                      {formState.buyAmount}{" "}
                      {formState.buyCurrency
                        ? " " + t(`${formState.buyCurrency}.currency.label`)
                        : ""}
                    </td>
                  </tr>
                  <tr>
                    <td>{t("you.want.to.sell.label")}</td>
                    <td>
                      {formState.sellAmount}{" "}
                      {formState.sellCurrency
                        ? " " + t(`${formState.sellCurrency}.currency.label`)
                        : ""}
                    </td>
                  </tr> */}
                  <tr>
                    <td>{t("payment.method.label")}</td>
                    <td>{formState.paymentMethod}</td>
                  </tr>
                  <tr>
                    <td>{t("deposit.account.label")}</td>
                    <td>{formState.depositAccountId}</td>
                  </tr>
                  <tr>
                    <td>{t("fee.label")}</td>
                    <td>
                      ${fee.toFixed(2)}{" "}
                      {formState.sellCurrency
                        ? " " + t(`${formState.sellCurrency}.currency.label`)
                        : ""}
                    </td>
                  </tr>
                  <tr>
                    <td>{t("total.label")}</td>
                    <td>
                      ${total.toFixed(2)}{" "}
                      {formState.sellCurrency
                        ? " " + t(`${formState.sellCurrency}.currency.label`)
                        : ""}
                    </td>
                  </tr>
                  <tr>
                    <td>{t("sub.total.label")}</td>
                    <td>
                      ${total.toFixed(2)}{" "}
                      {formState.sellCurrency
                        ? " " + t(`${formState.sellCurrency}.currency.label`)
                        : ""}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostComponent;
